import { useRef } from 'react';

import type { LngLat, YMap } from '@yandex/ymaps3-types';
import type { Feature } from '@yandex/ymaps3-types/packages/clusterer';

import { MapLoader } from '../../../shared/components/MapLoader';
import {
  Cluster,
  Hint,
  LocationMarker,
  YandexMap,
  YandexMapProvider,
  useYandexMap,
} from '../../../shared/components/YandexMap';
import { useCreateSquareImage } from '../../../shared/hooks/useCreateSquareImage';
import { CROP_SIZE } from '../lib/constants';
import type { CustomFeatureLocation } from '../lib/normalizeYandexMapData';
import { useNormalizeYandexMapData } from '../lib/normalizeYandexMapData';
import { useLocationMap } from '../model/LocationMapDataProvider';

interface YandexMapLocationCustomProps {
  url: string;
}

const SOURCE = 'source';

const YandexMapLocationPlanBase = (props: YandexMapLocationCustomProps) => {
  const { url } = props;
  const { image } = useCreateSquareImage({ url });
  const ymapRef = useRef<YMap>(null);
  const { ymapsInstance } = useYandexMap();
  const data = useLocationMap();
  const { zoom, locations, onMarkerClick, onMapClick, onDraggableMarker } = useNormalizeYandexMapData(data, ymapRef);

  if (!ymapsInstance || image?.width === 0 || image?.height === 0 || !image) {
    return <MapLoader />;
  }

  const { YMapListener, YMapFeatureDataSource, YMapLayer, YMapTileDataSource } = ymapsInstance.ymaps;
  const { Cartesian } = ymapsInstance.projection;
  const { YMapClusterer, clusterByGrid } = ymapsInstance.clusterer;

  const locationMarker = (feature: Feature) => {
    const updateFeature = feature as CustomFeatureLocation;
    return (
      <LocationMarker
        view='primary'
        source={SOURCE}
        ymapsInstance={ymapsInstance}
        draggable
        coordinates={updateFeature.geometry.coordinates}
        onClick={onMarkerClick(updateFeature.properties)}
        properties={updateFeature.properties}
        onDragEnd={onDraggableMarker(updateFeature.properties)}
      />
    );
  };

  const handleClusterClick = (coordinates: LngLat) => () => {
    ymapRef.current?.setLocation({
      center: coordinates,
      zoom: zoom + 2,
      duration: 300,
    });
  };

  const cluster = (coordinates: LngLat, features: Feature[]) => (
    <Cluster
      count={features.length}
      ymapsInstance={ymapsInstance}
      coordinates={coordinates}
      source={SOURCE}
      onClick={handleClusterClick(coordinates)}
    />
  );

  const method = clusterByGrid({ gridSize: 64 });

  return (
    <YandexMap
      ref={ymapRef}
      location={{ center: [0, 0], zoom: 1 }}
      mode='raster'
      worldOptions={{ cycledX: false }}
      projection={
        new Cartesian(
          [
            [-image.width, -image.height],
            [image.width, image.height],
          ],
          [false, false],
        )
      }
    >
      <YMapTileDataSource
        id='custom'
        raster={{
          type: 'ground',
          fetchTile: (x, y, z) =>
            new Promise((resolve) => {
              const canvas = document.createElement('canvas');
              canvas.width = CROP_SIZE;
              canvas.height = CROP_SIZE;

              const dimenssion = 2 ** z;
              const resize = CROP_SIZE * dimenssion;

              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(image, x * -CROP_SIZE, y * -CROP_SIZE, resize, resize);
              }

              resolve({ image: canvas });
            }),
        }}
      />
      <YMapListener onClick={onMapClick} />
      <YMapLayer source='custom' type='ground' zIndex={5} />
      <YMapFeatureDataSource id={SOURCE} />
      <YMapLayer source={SOURCE} type='markers' zIndex={1800} />
      <Hint ymapsInstance={ymapsInstance} />
      <YMapClusterer method={method} features={locations} marker={locationMarker} cluster={cluster} />
    </YandexMap>
  );
};

export const YandexMapLocationPlan = (props: YandexMapLocationCustomProps) => (
  <YandexMapProvider>
    <YandexMapLocationPlanBase {...props} />
  </YandexMapProvider>
);
